import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Grid,
  TextField,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { Settings, PhoneCall, RefreshCw } from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../services/api';
import { styled } from '@mui/material/styles';
import CircularProgress from '@mui/material/CircularProgress';

export default function SpokiSettingsPage() {
  const [loading, setLoading] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [channels, setChannels] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [accountDialogOpen, setAccountDialogOpen] = useState(false);
  const [channelDialogOpen, setChannelDialogOpen] = useState(false);
  const [accountForm, setAccountForm] = useState({
    spoki_account_id: '',
    account_name: '',
    account_api_key: ''
  });
  const [channelForm, setChannelForm] = useState({
    spoki_account_id: '',
    external_channel_id: '',
    phone: '',
    display_name: '',
    is_active: true,
    is_default_global: false
  });

  const loadSettings = async () => {
    try {
      setLoading(true);
      const res = await api.get('/spoki/settings');
      const data = res.data || {};
      setAccounts(data.accounts || []);
      setChannels(data.channels || []);
    } catch (error) {
      toast.error('Errore nel caricamento delle impostazioni Spoki');
    } finally {
      setLoading(false);
    }
  };

  const handleSyncAccounts = async () => {
    try {
      setSyncing(true);
      const res = await api.post('/spoki/settings/accounts/sync');
      const data = res.data || {};
      const count = typeof data.accounts === 'number' ? data.accounts : null;
      if (count !== null) {
        toast.success(`Sincronizzati ${count} account Spoki`);
      } else {
        toast.success('Sincronizzazione account Spoki completata');
      }
      await loadSettings();
    } catch (error) {
      toast.error('Errore nella sincronizzazione account Spoki');
      setLoading(false);
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  const handleCreateAccount = async () => {
    try {
      if (!accountForm.spoki_account_id || !accountForm.account_name || !accountForm.account_api_key) {
        toast.error('Compilare tutti i campi account');
        return;
      }
      await api.post('/spoki/settings/accounts', {
        ...accountForm
      });
      toast.success('Account Spoki salvato');
      setAccountForm({
        spoki_account_id: '',
        account_name: '',
        account_api_key: ''
      });
      loadSettings();
      setAccountDialogOpen(false);
    } catch (error) {
      toast.error('Errore nel salvataggio account Spoki');
    }
  };

  const handleCreateChannel = async () => {
    try {
      if (!channelForm.spoki_account_id || !channelForm.external_channel_id || !channelForm.phone) {
        toast.error('Compilare account, ID canale e telefono');
        return;
      }
      await api.post('/spoki/settings/channels', {
        ...channelForm,
        spoki_account_id: Number(channelForm.spoki_account_id)
      });
      toast.success('Canale Spoki salvato');
      setChannelForm({
        spoki_account_id: '',
        external_channel_id: '',
        phone: '',
        display_name: '',
        is_active: true,
        is_default_global: false
      });
      loadSettings();
      setChannelDialogOpen(false);
    } catch (error) {
      toast.error('Errore nel salvataggio canale Spoki');
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: '#eef2ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Settings size={20} color="#4f46e5" />
          </Box>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              Impostazioni Spoki
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Configurazione account Spoki e numeri WhatsApp mittenti
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            onClick={handleSyncAccounts}
            disabled={syncing}
            startIcon={syncing ? <CircularProgress size={20} color="inherit" /> : <RefreshCw size={20} />}
          >
            {syncing ? 'Sincronizzazione...' : 'Sincronizza'}
          </Button>
          <Button
            variant="contained"
            onClick={() => setAccountDialogOpen(true)}
          >
            Nuovo account
          </Button>
        </Box>
      </Box>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <StyledCard>
            <CardHeader
              title="Account Spoki"
              subheader="Elenco account Spoki disponibili (sincronizzati e locali)"
            />
            <CardContent>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Nome</TableCell>
                    <TableCell>ID</TableCell>
                    <TableCell>Stato</TableCell>
                    <TableCell>Quality</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {accounts.map((a) => (
                    <TableRow key={a.id}>
                      <TableCell>{a.account_name}</TableCell>
                      <TableCell>{a.spoki_account_id}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          color={a.is_active ? 'success' : 'default'}
                          label={a.is_active ? 'Attivo' : 'Disattivo'}
                        />
                      </TableCell>
                      <TableCell>{a.quality_score || '-'}</TableCell>
                    </TableRow>
                  ))}
                  {accounts.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <Typography variant="body2" color="text.secondary">
                          Nessun account configurato
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </StyledCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <StyledCard>
            <CardHeader
              title="Numeri / Canali"
              subheader="Gestisci i numeri mittenti disponibili"
              action={
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<PhoneCall size={18} />}
                  onClick={() => setChannelDialogOpen(true)}
                  disabled={accounts.length === 0}
                >
                  Nuovo canale
                </Button>
              }
            />
            <CardContent>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Numero</TableCell>
                    <TableCell>Account</TableCell>
                    <TableCell>Stato</TableCell>
                    <TableCell>Default</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {channels.map((c) => (
                    <TableRow key={c.id}>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Typography variant="body2">
                            {c.phone || '-'}
                          </Typography>
                          {c.display_name && (
                            <Typography variant="caption" color="text.secondary">
                              {c.display_name}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>{c.account_name}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          color={c.is_active ? 'success' : 'default'}
                          label={c.is_active ? 'Attivo' : 'Disattivo'}
                        />
                      </TableCell>
                      <TableCell>
                        {c.is_default_global ? (
                          <Chip
                            size="small"
                            color="primary"
                            label="Predefinito"
                          />
                        ) : (
                          '-'
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {channels.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <Typography variant="body2" color="text.secondary">
                          Nessun canale configurato
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </StyledCard>
        </Grid>
      </Grid>

      <Dialog open={accountDialogOpen} onClose={() => setAccountDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nuovo account Spoki</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12}>
              <TextField
                label="Spoki Account ID"
                value={accountForm.spoki_account_id}
                onChange={(e) =>
                  setAccountForm((prev) => ({
                    ...prev,
                    spoki_account_id: e.target.value
                  }))
                }
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Nome account"
                value={accountForm.account_name}
                onChange={(e) =>
                  setAccountForm((prev) => ({
                    ...prev,
                    account_name: e.target.value
                  }))
                }
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="API Key"
                type="password"
                value={accountForm.account_api_key}
                onChange={(e) =>
                  setAccountForm((prev) => ({
                    ...prev,
                    account_api_key: e.target.value
                  }))
                }
                fullWidth
                size="small"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAccountDialogOpen(false)} disabled={loading}>
            Annulla
          </Button>
          <Button onClick={handleCreateAccount} disabled={loading} variant="contained">
            Salva account
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={channelDialogOpen} onClose={() => setChannelDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nuovo canale Spoki</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel id="channel-account-label">Account</InputLabel>
                <Select
                  labelId="channel-account-label"
                  label="Account"
                  value={channelForm.spoki_account_id}
                  onChange={(e) =>
                    setChannelForm((prev) => ({
                      ...prev,
                      spoki_account_id: e.target.value
                    }))
                  }
                >
                  {accounts.map((a) => (
                    <MenuItem key={a.id} value={String(a.id)}>
                      {a.account_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="External Channel ID"
                value={channelForm.external_channel_id}
                onChange={(e) =>
                  setChannelForm((prev) => ({
                    ...prev,
                    external_channel_id: e.target.value
                  }))
                }
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Telefono"
                value={channelForm.phone}
                onChange={(e) =>
                  setChannelForm((prev) => ({
                    ...prev,
                    phone: e.target.value
                  }))
                }
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Nome visualizzato"
                value={channelForm.display_name}
                onChange={(e) =>
                  setChannelForm((prev) => ({
                    ...prev,
                    display_name: e.target.value
                  }))
                }
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={6}>
              <Button
                variant={channelForm.is_active ? 'contained' : 'outlined'}
                color={channelForm.is_active ? 'success' : 'inherit'}
                onClick={() =>
                  setChannelForm((prev) => ({
                    ...prev,
                    is_active: !prev.is_active
                  }))
                }
                fullWidth
              >
                {channelForm.is_active ? 'Attivo' : 'Disattivo'}
              </Button>
            </Grid>
            <Grid item xs={6}>
              <Button
                variant={channelForm.is_default_global ? 'contained' : 'outlined'}
                color={channelForm.is_default_global ? 'primary' : 'inherit'}
                onClick={() =>
                  setChannelForm((prev) => ({
                    ...prev,
                    is_default_global: !prev.is_default_global
                  }))
                }
                fullWidth
              >
                Predefinito
              </Button>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChannelDialogOpen(false)} disabled={loading}>
            Annulla
          </Button>
          <Button
            onClick={handleCreateChannel}
            disabled={loading || !channelForm.spoki_account_id}
            variant="contained"
            startIcon={<PhoneCall size={18} />}
          >
            Salva canale
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

const StyledCard = styled(Card)(({ theme }) => ({
  boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden'
}));
